import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ShopDTO } from '@wberp/shared';
import { api, type ShopCleanupItemDTO, type ShopCleanupPolicyDTO, type ShopCleanupPolicyInputDTO, type ShopCleanupRunDTO, type ShopCleanupRunDetailDTO } from './api';
import { ErrorBox, Loading, Modal } from './components';
import './shop-cleanup.css';

const DEFAULT_POLICY: ShopCleanupPolicyInputDTO = {
  enabled: false, shopIds: [], minAgeDays: 7, windowDays: 7, maxViews: 10,
  runAt: '03:00', timeZone: 'Asia/Shanghai', includeOutsideErp: false,
};
const ACTIVE = new Set<ShopCleanupRunDTO['status']>(['queued', 'running', 'waiting']);
const TERMINAL = new Set<ShopCleanupRunDTO['status']>(['completed', 'failed', 'cancelled']);
const STATUS: Record<ShopCleanupRunDTO['status'], string> = {
  queued: '排队中', running: '执行中', waiting: '等待 WB', completed: '已完成', failed: '失败', cancelled: '已停止',
};
const PHASE: Record<ShopCleanupRunDTO['phase'], string> = {
  scan: '扫描 WB 商品', analytics: '读取浏览量', evaluate: '筛选候选', cleanup: '清理 WB 库存', done: '已结束',
};
const ITEM_STATUS: Record<ShopCleanupItemDTO['status'], string> = {
  candidate: '候选', skipped: '保留', zeroing: '正在清零', confirming: '等待确认', confirmed: 'WB 已确认移入垃圾桶', failed: '失败',
};
const REASONS: Record<string, string> = {
  low_traffic: '整组浏览量低于阈值', views_at_or_above_threshold: '流量阈值已达到，保留整组',
  known_views_at_or_above_threshold: '已知部分浏览量已达到阈值，保留整组（总量未完整查询）',
  incomplete_analytics: '统计缺失，保留整组', unknown_or_young_card: '上架日期未知或上架保护期未满，保留整组',
  orders_present: '检查期间存在 WB 订单，保留整组', erp_orders_present: 'ERP 历史存在订单，保留整组',
  incomplete_order_analytics: '订单数据未核实，保留整组',
  outside_erp_disabled: '未开启 ERP 外商品清理，保留', partially_tracked_group: '整组 ERP 发布记录不完整，保留',
  missing_group_identity: '缺少颜色组信息，保留', uncertain_sku_mapping: 'SKU 或发布状态尚未确认，保留',
  ambiguous_sku_mapping: '规格对应关系不明确，保留', group_too_many_skus: '整组超过 1000 个 SKU，保留', group_too_large: '颜色组超出单批处理范围，保留',
  group_changed_since_scan: '扫描后颜色组或规格发生变化，保留', group_missing_since_scan: '扫描后组内商品缺失，保留',
  erp_publication_changed_since_scan: '发布状态、时间或库存回执发生变化，保留', outside_group_now_tracked_in_erp: '商品已有新的 ERP 发布记录，保留',
  no_valid_warehouses_stock_not_assumed_zero: '无法核实卖家仓库存，保留', inventory_not_confirmed_zero: '库存尚未确认归零',
  normal_absent_and_trash_present: 'WB 普通列表已移除，回收站已确认',
  trash_submitted_pending_confirmation: '已提交 WB，等待回收站确认', trash_outcome_pending_read_only_confirmation: 'WB 结果未确认，仅回查',
  stock_zeroed_trash_not_sent_rate_wait_over_2_minutes: '库存已清零，WB 限流导致未提交回收站，请核对',
  cleanup_stopped_rate_wait_before_inventory: 'WB 限流后停止，未修改该组库存',
};

function policyInput(policy: ShopCleanupPolicyDTO | null): ShopCleanupPolicyInputDTO {
  return policy ? {
    enabled: policy.enabled, shopIds: [...policy.shopIds], minAgeDays: policy.minAgeDays,
    windowDays: policy.windowDays, maxViews: policy.maxViews, runAt: policy.runAt,
    timeZone: policy.timeZone, includeOutsideErp: policy.includeOutsideErp,
  } : { ...DEFAULT_POLICY, shopIds: [] };
}
function samePolicy(a: ShopCleanupPolicyInputDTO, b: ShopCleanupPolicyInputDTO): boolean {
  return JSON.stringify({ ...a, shopIds: [...a.shopIds].sort() }) === JSON.stringify({ ...b, shopIds: [...b.shopIds].sort() });
}
function date(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.valueOf()) ? value : parsed.toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false });
}
function makeIdempotencyKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    return (char === 'x' ? random : (random & 0x3) | 0x8).toString(16);
  });
}

function runMetrics(run: ShopCleanupRunDTO): string {
  return `扫描 ${run.scannedCount} 张卡 · 预览候选 ${run.candidateCount} 组 · WB 确认移入垃圾桶 ${run.confirmedCount} 组 · 保留 ${run.skippedCount} 组 · 失败 ${run.failedCount} 组`;
}

function CleanupRetentionSummary({ run }: { run: ShopCleanupRunDTO }) {
  const summary = run.retentionSummary;
  if (!summary) return null;
  return <span className="cleanup-retention-summary" aria-label="保留原因汇总">保留原因：流量阈值已达到 {summary.trafficThreshold} 组 · 上架保护 {summary.ageProtection} 组 · 统计缺失 {summary.missingAnalytics} 组{summary.orderProtection != null && ` · 有订单保留 ${summary.orderProtection} 组`} · 其他 {summary.other} 组</span>;
}

function orderCountText(item: ShopCleanupItemDTO): string {
  return item.orderCount == null ? '未核实' : String(item.orderCount);
}

function CleanupAnalyticsProgress({ run }: { run: ShopCleanupRunDTO }) {
  const [now, setNow] = useState(Date.now);
  const next = run.nextRunAt ? Date.parse(run.nextRunAt) : NaN;
  const active = ACTIVE.has(run.status) && run.phase === 'analytics';
  useEffect(() => {
    setNow(Date.now());
    if (!active || !Number.isFinite(next)) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active, next]);
  const progress = run.analyticsProgress;
  if (!progress) return active ? <span className="field-help">此旧任务未记录补查进度；浏览量读取结束后才计算候选与保留数量。</span> : null;
  const seconds = Number.isFinite(next) ? Math.max(0, Math.ceil((next - now) / 1000)) : 0;
  const countdown = seconds >= 60 ? `${Math.floor(seconds / 60)} 分 ${seconds % 60} 秒` : `${seconds} 秒`;
  return <div className="cleanup-analytics-progress">
    <div className="cleanup-analytics-counts"><span>已补查 <strong>{progress.checkedCount}</strong> 张</span><span>{ACTIVE.has(run.status) ? '待补查' : '未补查'} <strong>{progress.pendingCount}</strong> 张</span>
      {active && <span className="cleanup-analytics-next" title="显示后台最早可再次执行时间，归零后由后台调度；不会增加 WB 请求">{!Number.isFinite(next) ? '下次请求时间待确定' : seconds > 0 ? `下次请求：${countdown}后` : run.status === 'running' ? '正在读取 WB' : '等待后台发起下次请求'}</span>}</div>
    <div className="field-help">本次实际请求结果：补齐 {progress.recoveredCount} 张，仍缺失 {progress.unresolvedCount} 张。
      {progress.awaitingPeriodCount != null && progress.awaitingPeriodCount > 0 && ` 另有 ${progress.awaitingPeriodCount} 张待期间查询，确认缺失后才计入待补查。`}
      {progress.unavailableCount > 0 && ` ${progress.unavailableCount} 张超出每日统计覆盖范围，继续保留。`}
      {active && ' 浏览量读取结束后再计算候选与保留数量。'}</div>
    {(progress.cachedMissingCount != null || progress.notRequiredCount != null) && <div className="field-help cleanup-analytics-cache">{progress.cachedMissingCount != null && `复用 12 小时内缺失统计缓存 ${progress.cachedMissingCount} 张。`}{progress.notRequiredCount != null && ` 已知流量达到保留阈值，${progress.notRequiredCount} 张商品卡无需补查日统计。`}</div>}
  </div>;
}

function viewsText(item: ShopCleanupItemDTO): string {
  if (item.views !== null) return String(item.views);
  if (item.reason === 'known_views_at_or_above_threshold') return '部分数据已达阈值（总量未完整查询）';
  return ['unknown_or_young_card', 'outside_erp_disabled', 'partially_tracked_group', 'missing_group_identity'].includes(item.reason)
    ? '未查询浏览量' : '未取得浏览量';
}

function CleanupRunDetails({ run, onClose, initialDetail, poll = true, allowRecheck = false }: { run: ShopCleanupRunDTO; onClose: () => void; initialDetail?: ShopCleanupRunDetailDTO; poll?: boolean; allowRecheck?: boolean }) {
  const [detail, setDetail] = useState<ShopCleanupRunDetailDTO | null>(initialDetail ?? null);
  const [loading, setLoading] = useState(!initialDetail);
  const usedInitialDetail = useRef(false);
  const [error, setError] = useState<Error | null>(null);
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const [page, setPage] = useState(0);
  const [revision, setRevision] = useState(0);
  const [rechecking, setRechecking] = useState(false);
  const [recheckMessage, setRecheckMessage] = useState<string | null>(null);
  const recheckBusy = useRef(false);
  const mounted = useRef(true);
  const cursor = cursors[page];
  const currentRun = detail?.run ?? run;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (initialDetail && !usedInitialDetail.current) { usedInitialDetail.current = true; return; }
    let active = true;
    setLoading(true); setError(null); setDetail(null);
    void api.getShopCleanupRun(run.id, cursor).then((value) => {
      if (active) setDetail(value);
    }).catch((reason: unknown) => {
      if (active) setError(reason instanceof Error ? reason : new Error('读取清理记录详情失败'));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [run.id, cursor, revision]);

  const active = ACTIVE.has(run.status) && (!detail || ACTIVE.has(detail.run.status));
  useEffect(() => {
    if (!poll || !active || loading || error) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') setRevision((value) => value + 1);
    }, 10_000);
    return () => window.clearInterval(timer);
  }, [active, loading, error, poll]);

  function nextPage(): void {
    if (!detail?.items.nextCursor) return;
    setCursors((current) => [...current.slice(0, page + 1), detail.items.nextCursor]);
    setPage((current) => current + 1);
  }

  async function recheck(): Promise<void> {
    if (!allowRecheck || !currentRun.confirmationPending || !['failed', 'waiting', 'queued'].includes(currentRun.status) || recheckBusy.current || loading) return;
    recheckBusy.current = true; setRechecking(true); setError(null); setRecheckMessage(null);
    try {
      const updated = await api.recheckShopCleanupRun(run.id);
      if (!mounted.current) return;
      setRecheckMessage(updated.confirmationPending ? '已请求只读回查，仍待 WB 回收站确认。不会重复清零库存或提交清理；请稍后手动刷新明细。' : '回查请求已接受，已重新读取明细。请核对最新确认结果。');
      setPage(0); setCursors([undefined]); setRevision((value) => value + 1);
    } catch (reason) {
      if (mounted.current) setError(reason instanceof Error ? reason : new Error('回查 WB 清理结果失败，请刷新后重试'));
    } finally {
      recheckBusy.current = false;
      if (mounted.current) setRechecking(false);
    }
  }

  return <Modal title="WB 清理运行详情" description={`${run.shopName} · ${run.mode === 'preview' ? '预览' : '执行'} · 运行 ID：${run.id}`} wide onClose={onClose} actions={<>
    {allowRecheck && currentRun.confirmationPending && <button className="button" disabled={loading || rechecking || !['failed', 'waiting', 'queued'].includes(currentRun.status)} onClick={() => void recheck()}>{rechecking ? '正在请求回查…' : '重新确认结果'}</button>}
    <button className="button" onClick={onClose}>关闭详情</button>
  </>}>
    <div className="cleanup-detail-summary"><span>{runMetrics(detail?.run ?? run)}</span><CleanupRetentionSummary run={detail?.run ?? run} /><CleanupAnalyticsProgress run={detail?.run ?? run} /><span>扫描按商品卡统计；候选、保留和处理结果按关联组统计。保留项不会执行清理。</span></div>
    {allowRecheck && currentRun.confirmationPending && <p className="field-help">WB 清理回执尚未确认。“重新确认结果”只回查原清理结果，不会重复清零库存或提交回收站操作；后台正在执行时请稍后刷新。</p>}
    {recheckMessage && <div className="notice" role="status">{recheckMessage}</div>}
    <div className="cleanup-detail-tools"><span>{detail ? `第 ${page + 1} 页${detail.items.total === undefined ? '' : ` · 共 ${detail.items.total} 组`} · 更新于 ${date(detail.run.updatedAt)}` : `第 ${page + 1} 页`}</span><button className="button button-small" disabled={loading || rechecking} onClick={() => setRevision((value) => value + 1)}>刷新明细</button></div>
    {error && <ErrorBox error={error} onRetry={() => setRevision((value) => value + 1)} />}
    {loading ? <Loading label="正在读取商品明细…" /> : detail && <>
      {detail.items.items.length ? <div className="cleanup-detail-table"><table><thead><tr><th>颜色组 ID</th><th>WB ID</th><th>卖家货号</th><th>浏览量</th><th>检查期 WB 订单</th><th>结果与原因</th></tr></thead><tbody>{detail.items.items.map((item) => <tr className="cleanup-item" key={item.id}>
        <td>{item.groupKey}</td><td>{item.nmIds.map((id) => <a key={id} href={`https://www.wildberries.ru/catalog/${id}/detail.aspx`} target="_blank" rel="noreferrer">{id}</a>)}</td>
        <td>{item.vendorCodes.map((code) => <span key={code}>{code}</span>)}</td><td>{viewsText(item)}</td><td>{orderCountText(item)}</td>
        <td><strong>{ITEM_STATUS[item.status]}</strong><span className={item.status === 'skipped' ? 'cleanup-retained' : ''}>{REASONS[item.reason] ?? item.reason}{item.hasHistoricalOrders === true && ' · ERP 历史有订单'}</span></td>
      </tr>)}</tbody></table></div> : <div className="field-help">该记录还没有商品明细。</div>}
      <div className="cleanup-detail-pagination"><button className="button button-small" disabled={loading || rechecking || page === 0} onClick={() => setPage((current) => current - 1)}>上一页</button><span>第 {page + 1} 页</span><button className="button button-small" disabled={loading || rechecking || !detail.items.nextCursor} onClick={nextPage}>下一页</button></div>
    </>}
  </Modal>;
}

// Mounted only for company administrators. The endpoint checks company access again.
export function ShopCleanupLinkedRun() {
  const [runId] = useState(() => {
    const value = new URLSearchParams(window.location.search).get('cleanupRunId');
    return value && /^[a-f\d]{8}-(?:[a-f\d]{4}-){3}[a-f\d]{12}$/iu.test(value) ? value : null;
  });
  const [closed, setClosed] = useState(false);
  const [detail, setDetail] = useState<ShopCleanupRunDetailDTO | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!runId || closed) return;
    let active = true;
    setError(null);
    void api.getShopCleanupRun(runId).then((value) => { if (active) setDetail(value); }).catch((reason: unknown) => {
      if (active) setError(reason instanceof Error ? reason : new Error('读取清理记录失败'));
    });
    return () => { active = false; };
  }, [runId, closed, revision]);
  function close(): void {
    setClosed(true);
    const url = new URL(window.location.href);
    url.searchParams.delete('cleanupRunId');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }
  if (!runId || closed) return null;
  if (detail) return <CleanupRunDetails run={detail.run} initialDetail={detail} poll={false} allowRecheck onClose={close} />;
  return <Modal title="WB 清理运行详情" onClose={close} actions={<button className="button" onClick={close}>关闭详情</button>}>
    {error ? <ErrorBox error={error} onRetry={() => setRevision((value) => value + 1)} /> : <Loading label="正在读取清理记录…" />}
  </Modal>;
}

export function ShopCleanup({ shops }: { shops: ShopDTO[] }) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const [saved, setSaved] = useState<ShopCleanupPolicyDTO | null>(null);
  const [draft, setDraft] = useState<ShopCleanupPolicyInputDTO>(DEFAULT_POLICY);
  const [runs, setRuns] = useState<ShopCleanupRunDTO[]>([]);
  const [runsNextCursor, setRunsNextCursor] = useState<string | undefined>();
  const [historyLoading, setHistoryLoading] = useState(false);
  const [detailRun, setDetailRun] = useState<ShopCleanupRunDTO | null>(null);
  const detailTrigger = useRef<HTMLButtonElement | null>(null);
  const [confirmMode, setConfirmMode] = useState<'preview' | 'execute' | null>(null);
  const [previewRunIds, setPreviewRunIds] = useState<string[] | null>(null);
  const [previewSnapshots, setPreviewSnapshots] = useState<ShopCleanupRunDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const generation = useRef(0);
  const activeRuns = useMemo(() => runs.filter((run) => ACTIVE.has(run.status)), [runs]);
  const previewRuns = useMemo(() => previewRunIds?.map((id) => runs.find((run) => run.id === id) ?? previewSnapshots.find((run) => run.id === id)).filter((run): run is ShopCleanupRunDTO => Boolean(run)) ?? null, [previewRunIds, previewSnapshots, runs]);
  const previewActive = Boolean(previewRuns?.some((run) => !TERMINAL.has(run.status)) || activeRuns.some((run) => run.mode === 'preview' && draft.shopIds.includes(run.shopId)));
  const dirty = saved ? !samePolicy(draft, policyInput(saved)) : false;
  const activeShops = useMemo(() => shops.filter((shop) => shop.status === 'active' && !shop.unbind), [shops]);
  const shopNames = useMemo(() => new Map(shops.map((shop) => [shop.id, shop.name])), [shops]);

  const load = useCallback(async () => {
    const requestId = ++generation.current;
    try {
      const overview = await api.getShopCleanup();
      if (requestId !== generation.current) return;
      setSaved(overview.policy);
      setDraft((current) => saved && !samePolicy(current, policyInput(saved)) ? current : policyInput(overview.policy));
      setRuns(overview.runs.items);
      setRunsNextCursor(overview.runs.nextCursor);
      setError(null);
    } catch (reason) {
      if (requestId === generation.current) setError(reason instanceof Error ? reason : new Error('读取 WB 清理配置失败'));
    } finally {
      if (requestId === generation.current) setLoading(false);
    }
  }, [saved]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    void load();
    return () => { generation.current++; };
  }, [open]); // refresh when the settings dialog opens; preserve unsaved edits

  useEffect(() => {
    if (!open || !activeRuns.length) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      const requestId = ++generation.current;
      void (async () => {
        try {
          const page = await api.getShopCleanupRuns();
          if (requestId !== generation.current) return;
          const refreshed = [...page.items];
          const found = new Set(refreshed.map((run) => run.id));
          let cursor = page.nextCursor;
          let pages = 1;
          while (cursor && previewRunIds?.some((id) => !found.has(id)) && pages < 10) {
            const older = await api.getShopCleanupRuns(cursor);
            if (requestId !== generation.current) return;
            for (const run of older.items) { refreshed.push(run); found.add(run.id); }
            cursor = older.nextCursor;
            pages++;
          }
          setRuns((current) => [...refreshed, ...current.filter((old) => !refreshed.some((item) => item.id === old.id))]);
          setError(null);
        } catch (reason) { if (requestId === generation.current) setError(reason instanceof Error ? reason : new Error('读取 WB 清理进度失败')); }
      })();
    }, 10_000);
    return () => window.clearInterval(timer);
  }, [open, activeRuns.length, previewRunIds]);

  function close(): void {
    setOpen(false);
    setConfirmMode(null);
    setDetailRun(null);
    trigger.current?.focus();
  }

  function change(patch: Partial<ShopCleanupPolicyInputDTO>): void {
    setDraft((current) => ({ ...current, ...patch }));
  }
  function toggleShop(id: string, checked: boolean): void {
    const shopIds = new Set(draft.shopIds);
    if (checked && shopIds.size >= 200) return;
    checked ? shopIds.add(id) : shopIds.delete(id);
    change({ shopIds: [...shopIds] });
  }
  async function save(): Promise<void> {
    const policyChanged = !saved || !samePolicy(draft, policyInput(saved));
    setBusy(true); setError(null);
    try {
      const updated = await api.saveShopCleanupPolicy(draft);
      setSaved(updated); setDraft(policyInput(updated));
      if (policyChanged) { setPreviewRunIds(null); setPreviewSnapshots([]); }
    } catch (reason) { setError(reason instanceof Error ? reason : new Error('保存清理计划失败')); }
    finally { setBusy(false); }
  }
  async function loadMoreRuns(): Promise<void> {
    if (!runsNextCursor || historyLoading) return;
    setHistoryLoading(true);
    try {
      const page = await api.getShopCleanupRuns(runsNextCursor);
      setRuns((current) => [...current, ...page.items.filter((item) => !current.some((old) => old.id === item.id))]);
      setRunsNextCursor(page.nextCursor);
    } catch (reason) { setError(reason instanceof Error ? reason : new Error('读取更多运行记录失败')); }
    finally { setHistoryLoading(false); }
  }
  async function begin(mode: 'preview' | 'execute'): Promise<void> {
    if (!saved || dirty) { setError(new Error('请先保存当前配置，再进行预览或执行。')); return; }
    if (draft.shopIds.length === 0) { setError(new Error('请至少选择一家当前启用的 WB 店铺。')); return; }
    if (mode === 'execute' && previewActive) { setError(new Error('请等待当前预览完成后，再确认执行。')); return; }
    if (mode === 'execute' && !previewRuns) { setConfirmMode('preview'); return; }
    if (mode === 'execute') { setConfirmMode('execute'); return; }
    await submitRun('preview');
  }
  async function submitRun(mode: 'preview' | 'execute'): Promise<void> {
    setBusy(true); setError(null); setConfirmMode(null);
    try {
      const result = await api.startShopCleanup({ mode, idempotencyKey: makeIdempotencyKey() });
      setRuns((current) => [...result.items, ...current.filter((old) => !result.items.some((run) => run.id === old.id))]);
      if (mode === 'preview') { setPreviewRunIds(result.items.map((run) => run.id)); setPreviewSnapshots(result.items); }
    } catch (reason) { setError(reason instanceof Error ? reason : new Error('启动 WB 清理任务失败')); }
    finally { setBusy(false); }
  }
  async function cancel(run: ShopCleanupRunDTO): Promise<void> {
    setBusy(true); setError(null);
    try {
      const updated = await api.cancelShopCleanupRun(run.id);
      setRuns((current) => current.map((item) => item.id === updated.id ? updated : item));
    } catch (reason) { setError(reason instanceof Error ? reason : new Error('停止清理任务失败')); }
    finally { setBusy(false); }
  }
  async function recheck(run: ShopCleanupRunDTO): Promise<void> {
    setBusy(true); setError(null);
    try {
      const updated = await api.recheckShopCleanupRun(run.id);
      setRuns((current) => current.map((item) => item.id === updated.id ? updated : item));
    } catch (reason) { setError(reason instanceof Error ? reason : new Error('回查 WB 清理结果失败')); }
    finally { setBusy(false); }
  }
  const latestRun = runs[0];

  return <>
    <button ref={trigger} type="button" className="button" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>WB 商品清理</button>
    {open && <Modal title="WB 低流量商品清理" description="统一设置公司店铺的清理规则、定时计划，并查看运行记录。" wide onClose={close} actions={<>
      <button className="button" onClick={close}>关闭</button>
      {!loading && <><button className="button button-primary" disabled={busy} onClick={() => void save()}>{busy ? '处理中…' : '保存配置'}</button><button className="button" disabled={busy || dirty || !saved} onClick={() => void begin('preview')}>预览</button><button className="button button-danger" disabled={busy || dirty || !saved || previewActive} onClick={() => void begin('execute')}>手动执行</button></>}
    </>}>
    <div className="cleanup-body">
      {error && <ErrorBox error={error} onRetry={() => void load()} />}
      {loading ? <Loading label="正在读取 WB 清理计划…" /> : <>
        <div className="cleanup-scope-note"><strong>仅影响 WB 店铺库存与垃圾桶。</strong> 按颜色组筛选和操作，一个颜色组命中后会整体处理该组。ERP 商品、图片、预处理、SKU 和历史记录都会保留。清理通过卖家仓库存清零并请求移入 WB 垃圾桶完成；WB 托管仓库存需另行核对。</div>
        <div className="cleanup-order-protection" role="note"><strong>订单保护（固定启用，不能关闭）：</strong>检查日期范围内存在任何 WB 订单，或 ERP 同步历史中存在订单，整组保留；订单数据未核实也整组保留。旧运行记录没有订单统计时会显示“未核实”，不会按 0 单处理。</div>
        <div className="cleanup-form-grid">
          <label className="cleanup-enabled"><input type="checkbox" checked={draft.enabled} onChange={(event) => change({ enabled: event.target.checked })} /> 启用定时清理</label>
          <div className="field"><label htmlFor="cleanup-min-age">上架满（天）</label><input id="cleanup-min-age" type="number" min={1} max={3650} value={draft.minAgeDays} onChange={(event) => change({ minAgeDays: Number(event.target.value) })} /><span className="field-help">ERP 跟踪商品按最近发布完成时间保守计算；ERP 外 WB 商品按 WB 卡片创建时间计算。</span></div>
          <div className="field"><label htmlFor="cleanup-window-days">检查日期范围（天）</label><input id="cleanup-window-days" type="number" min={1} max={365} value={draft.windowDays} onChange={(event) => change({ windowDays: Number(event.target.value) })} /><span className="field-help">检查运行当天之前已完整结束的日期。</span></div>
          <div className="field"><label htmlFor="cleanup-max-views">浏览量上限</label><input id="cleanup-max-views" type="number" min={0} max={1000000000} value={draft.maxViews} onChange={(event) => change({ maxViews: Number(event.target.value) })} /><span className="field-help">浏览量指 WB 商品卡片打开次数（openCount），必须低于上限。</span></div>
          <div className="field"><label htmlFor="cleanup-run-at">每日运行时间</label><input id="cleanup-run-at" type="time" value={draft.runAt} onChange={(event) => change({ runAt: event.target.value })} /><span className="field-help">时区：Asia/Shanghai</span></div>
        </div>
        <fieldset className="cleanup-shop-picker"><legend>选择店铺</legend><div className="cleanup-shop-select-actions"><button type="button" className="button button-small" onClick={() => change({ shopIds: activeShops.slice(0, 200).map((shop) => shop.id) })}>全选有效店铺</button><button type="button" className="button button-small" onClick={() => change({ shopIds: [] })}>清空选择</button></div><div className="cleanup-shop-list">{activeShops.length ? activeShops.map((shop) => <label key={shop.id}><input type="checkbox" checked={draft.shopIds.includes(shop.id)} disabled={!draft.shopIds.includes(shop.id) && draft.shopIds.length >= 200} onChange={(event) => toggleShop(shop.id, event.target.checked)} />{shop.name}</label>) : <span className="field-help">没有当前启用的 WB 店铺。</span>}</div><span className="field-help">仅使用当前启用的店铺。新添加的店铺不会自动加入计划；最多选择200家。</span></fieldset>
        <label className="cleanup-outside-erp"><input type="checkbox" checked={draft.includeOutsideErp} onChange={(event) => change({ includeOutsideErp: event.target.checked })} /><span><strong>包含 ERP 外的 WB 商品</strong><span>开启后会检查整个 WB 店铺中的商品，包括未由 ERP 管理的商品；这些商品一旦满足条件也会被移入 WB 垃圾桶。</span></span></label>
        <div className="cleanup-policy-meta">已选 {draft.shopIds.length} 家 · 定时状态：{saved?.enabled ? '启用' : '停用'} · 每日 {saved?.runAt ?? draft.runAt}（{saved?.timeZone ?? draft.timeZone}） · 上次运行：{latestRun ? `${date(latestRun.createdAt)}（${STATUS[latestRun.status]}）` : '—'} · 下次运行：{date(saved?.nextRunAt)}</div>
        <div className="field-help cleanup-manual-note">手动预览和执行不依赖“启用定时清理”；保存规则后，可单独确认手动任务。</div>
        {dirty && <div className="cleanup-dirty">配置有未保存修改。请先保存，预览和手动执行只会使用已保存规则。</div>}
        {previewRuns && <div className="cleanup-preview-result" role="status">{previewActive ? `预览进行中：${previewRuns.filter((run) => !TERMINAL.has(run.status)).length} 个店铺任务尚未结束。` : `最近预览完成：${previewRuns.reduce((sum, run) => sum + run.candidateCount, 0)} 组候选，${previewRuns.reduce((sum, run) => sum + run.skippedCount, 0)} 组保留。`} 查看商品 ID 和筛选原因，请点击运行记录右侧“详情”。预览不会执行清理。</div>}
        {previewActive && <div className="cleanup-dirty">当前有预览任务运行中；请等待预览结束后再手动执行。</div>}
        <section className="cleanup-runs"><div className="cleanup-section-heading"><h3>运行记录</h3><span>预览候选与 WB 实际确认移入垃圾桶分别统计</span></div>
          {!runs.length ? <div className="field-help">暂无运行记录。</div> : <div className="cleanup-run-list">{runs.map((run) => <article key={run.id} className={`cleanup-run ${detailRun?.id === run.id ? 'is-selected' : ''}`}>
            <div className="cleanup-run-main"><span className="cleanup-run-title">{run.shopName} · {run.mode === 'preview' ? '预览' : '执行'} · {run.trigger === 'scheduled' ? '定时' : '手动'}</span><span className="cleanup-run-sub">{date(run.createdAt)} · {STATUS[run.status]} · {PHASE[run.phase]}</span><span className="cleanup-run-metrics">{runMetrics(run)}</span><CleanupRetentionSummary run={run} /><CleanupAnalyticsProgress run={run} /><span className="cleanup-run-id">运行 ID：{run.id}</span>{run.message && <span className={`cleanup-run-message${run.status === 'failed' || run.confirmationPending ? ' is-error' : ''}`}>{run.message}</span>}</div>
            <div className="cleanup-run-actions"><button type="button" className="button button-small" aria-haspopup="dialog" onClick={(event) => { detailTrigger.current = event.currentTarget; setDetailRun(run); }}>详情</button>{ACTIVE.has(run.status) && <button className="button button-small button-danger" disabled={busy} onClick={() => void cancel(run)}>停止</button>}{run.status === 'failed' && run.confirmationPending && <button className="button button-small" disabled={busy} onClick={() => void recheck(run)}>重新确认结果</button>}</div>
            {run.confirmationPending && <span className="cleanup-run-message is-error">WB 回执不明，店铺写入暂缓。请在 WB 后台核对原清理结果，再重新确认；此按钮只回查，不重复清理。</span>}
          </article>)}</div>}
          {runsNextCursor && <button className="button button-small cleanup-history-more" disabled={historyLoading} onClick={() => void loadMoreRuns()}>{historyLoading ? '读取中…' : '加载更多运行记录'}</button>}
        </section>
      </>}
    </div>
    </Modal>}
    {open && detailRun && <CleanupRunDetails key={detailRun.id} run={runs.find((run) => run.id === detailRun.id) ?? detailRun} onClose={() => { setDetailRun(null); detailTrigger.current?.focus(); }} />}
    {confirmMode && <Modal title={confirmMode === 'preview' ? '先生成当前规则的预览' : '确认手动执行 WB 清理'} description="确认前请核对店铺与规则。" onClose={() => setConfirmMode(null)} actions={<><button className="button" onClick={() => setConfirmMode(null)}>返回</button><button className={`button ${confirmMode === 'execute' ? 'button-danger' : 'button-primary'}`} disabled={busy} onClick={() => void submitRun(confirmMode)}>{busy ? '提交中…' : confirmMode === 'execute' ? '确认执行' : '生成预览'}</button></>}>
      <div className="cleanup-confirm"><p><strong>店铺：</strong>{draft.shopIds.map((id) => shopNames.get(id) ?? id).join('、')}</p><p><strong>规则：</strong>商品年龄至少 {draft.minAgeDays} 天（ERP 跟踪商品按最近发布完成时间保守计算，ERP 外 WB 商品按 WB 卡片创建时间），检查最近 {draft.windowDays} 个完整日期，卡片打开次数低于 {draft.maxViews}，每天 {draft.runAt}（Asia/Shanghai）；{draft.includeOutsideErp ? '包含 ERP 外 WB 商品' : '仅 ERP 跟踪的 WB 商品'}。</p>{confirmMode === 'execute' && previewRuns && <p><strong>最近预览：</strong>{previewRuns.reduce((sum, run) => sum + run.candidateCount, 0)} 个候选，{previewRuns.reduce((sum, run) => sum + run.skippedCount, 0)} 个保留；范围如已变化，请重新预览。</p>}<div className="cleanup-confirm-warning">{confirmMode === 'execute' ? '执行会对候选商品调用 WB 库存清零并请求移入 WB 垃圾桶。此操作会影响 WB 店铺商品展示；停止或失败后，已清零的库存不会自动恢复。' : '预览会读取 WB 商品和浏览量，不会修改 WB 商品。'} ERP 商品、图片、预处理、SKU 和历史记录不会删除。</div></div>
    </Modal>}
  </>;
}
